import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useCoordinatorSession } from '../../context/TrackerContext';
import { crmFileUrl, meet } from '../../crm/crmApi';
import type { CoordAuth, CoordLeadData, CoordStatus, CrmActivity, CrmLead, IncentiveRow, Meeting, MeetTimesData, MineData, PollRules } from '../../crm/crmApi';
import { ICON, meetingLabel, nextLabel, pollLabel } from '../../crm/LeadPanel';
import { CancelMeetingModal, JoinCode, MeetNumbers, MeetingChip, whatsappUrl } from '../../crm/Meetings';
import { Empty, Modal, Spinner, StageChip, Tabs, fmtHm, fmtPhone, fmtWhen, fromLocalInput, inputCls, labelCls, purpleBg, toLocalInput } from '../../crm/ui';

// ── A Student Coordinator's sales meetings ───────────────────
// Salespeople book the student a 1-hour meeting in one of the coordinator's
// free times (Meeting Times). The coordinator adds the meeting link (Google
// Meet, Zoom, Teams, or a whole Teams invite), and then the student's page
// opens: their details and the call log. After the meeting they mark whether
// the student joined: joined → they send the brochure then and fix the
// poll's time with the student (a reminder comes at that time; the status is
// due a few hours after it), and handle the lead from there (the poll's
// status, Converted with one tap once they paid: the payment record is the
// student's own registration form); didn't join → it goes to the
// salesperson's No Response list. Once the student has paid the whole before-placement fee the
// coordinator earns 10% of it (My Incentive). Mirrors the app's
// coordinator_meetings_screen.dart.

const NOT_INTERESTED = ['Fees too high', 'Wants offline classes', 'Wants a job, not a course', "Course doesn't fit", 'Joined somewhere else', 'Family not agreeing', "Timing doesn't suit", 'Other'];
const rupees = (n: number) => `₹${n.toLocaleString('en-IN')}`;
const minutesAgo = (m: number) => toLocalInput(new Date(Date.now() - m * 60000));
// Today at 5:30 PM (the poll time most use), while it is still ahead
function fiveThirty(): string {
  const d = new Date();
  d.setHours(17, 30, 0, 0);
  return d.getTime() > Date.now() + 5 * 60000 ? toLocalInput(d) : '';
}
const isLater = (v: string) => !!v && new Date(fromLocalInput(v)).getTime() > Date.now() + 60000;
// The server's rule: from 3 days back, or any time ahead
function pollTimeProblem(v: string): string {
  const t = v ? new Date(fromLocalInput(v)).getTime() : NaN;
  if (!v || isNaN(t)) return 'Pick the poll time';
  if (t < Date.now() - 3 * 86400e3) return 'Pick a poll time in the last 3 days';
  return '';
}

const Header: React.FC<{ icon: string; title: string; sub: string; right?: React.ReactNode }> = ({ icon, title, sub, right }) => (
  <div className="rounded-2xl overflow-hidden shadow-lg" style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
    <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <div className="flex items-center gap-2 mb-1"><span className="text-2xl" aria-hidden>{icon}</span><h1 className="text-xl font-bold text-white">{title}</h1></div>
        <p className="text-white/50 text-xs max-w-2xl">{sub}</p>
      </div>
      {right && <div className="flex gap-2 flex-wrap">{right}</div>}
    </div>
  </div>
);

const refreshBtn = (onClick: () => void) => (
  <button onClick={onClick} className="px-3 py-2 rounded-xl text-xs font-semibold border border-white/20 text-white/80 hover:bg-white/10">↻ Refresh</button>
);

// The session's sign-in for the meetings (a missing one: sign in again)
function useCoordAuth(): CoordAuth | null {
  const s = useCoordinatorSession();
  return s.coordToken ? { coordToken: s.coordToken } : null;
}
const SignInAgain = () => <Empty icon="🔐" title="Sign out and sign in again" sub="The sales meetings need the new sign-in." />;

const ReasonPick: React.FC<{ value: string; other: string; onValue: (v: string) => void; onOther: (v: string) => void }> = ({ value, other, onValue, onOther }) => (
  <div className="space-y-2">
    <select value={value} onChange={e => onValue(e.target.value)} className={inputCls} aria-label="Reason">
      <option value="">— Pick a reason —</option>
      {NOT_INTERESTED.map(r => <option key={r} value={r}>{r}</option>)}
    </select>
    {value === 'Other' && <input value={other} onChange={e => onOther(e.target.value)} placeholder="Type the reason" className={inputCls} aria-label="The reason" />}
  </div>
);

// When the poll goes out: the time fixed with the student (now, or any time
// later: a reminder comes then), or a while ago if it went already
const PollTime: React.FC<{ value: string; onChange: (v: string) => void; id: string; poll: PollRules }> = ({ value, onChange, id, poll }) => {
  const at530 = fiveThirty();
  const chips: [string, string][] = [
    ['Now', minutesAgo(0)], ['In 1 hour', minutesAgo(-60)], ['In 2 hours', minutesAgo(-120)],
    ...(at530 ? [['5:30 PM today', at530] as [string, string]] : []),
    ...(poll.pollMorning ? [[fmtWhen(poll.pollMorning), toLocalInput(poll.pollMorning)] as [string, string]] : []),
    ['30 min ago', minutesAgo(30)],
  ];
  return (
    <div>
      <label className={labelCls} htmlFor={id}>Poll time (fixed with the student)</label>
      <input id={id} type="datetime-local" value={value} onChange={e => onChange(e.target.value)} className={inputCls} />
      <div className="flex flex-wrap gap-1.5 mt-2">
        {chips.map(([label, v]) => (
          <button key={label} type="button" onClick={() => onChange(v)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-gray-200 text-gray-600 hover:border-purple-300">{label}</button>
        ))}
      </div>
      <p className="text-xs text-fuchsia-900 bg-fuchsia-50 rounded-xl px-3 py-2 mt-2">
        {isLater(value) ? `You get a reminder at ${fmtWhen(fromLocalInput(value))} to send the poll. ` : ''}Update its status within {poll.pollHours} hours of it going out.
      </p>
    </div>
  );
};

// ── After the meeting: did they join? ────────────────────────

const OutcomeModal: React.FC<{ auth: CoordAuth; m: Meeting; poll: PollRules; onClose: () => void; onSaved: () => void }> = ({ auth, m, poll, onClose, onSaved }) => {
  const [joined, setJoined] = useState<'' | 'yes' | 'no'>('');
  const [next, setNext] = useState<'poll' | 'not-interested'>('poll');
  const [pollAt, setPollAt] = useState(minutesAgo(0));
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!joined) { toast.error('Did the student join?'); return; }
    const why = reason === 'Other' ? other.trim() : reason;
    if (joined === 'yes' && next === 'not-interested' && !why) { toast.error('Say why they are not interested'); return; }
    if (joined === 'yes' && next === 'poll' && pollTimeProblem(pollAt)) { toast.error(pollTimeProblem(pollAt)); return; }
    setSaving(true);
    try {
      await meet.outcome(auth, {
        meetingId: m.id, joined: joined === 'yes', note: note.trim(),
        ...(joined === 'yes' ? (next === 'poll' ? { next, pollSentAt: fromLocalInput(pollAt) } : { next, reason: why }) : {}),
      });
      toast.success(joined === 'no' ? `${m.name}: on ${m.salesPersonName ? `${m.salesPersonName}'s` : "the salesperson's"} No Response list`
        : next === 'not-interested' ? `${m.name}: not interested`
        : isLater(pollAt) ? `${m.name}: brochure sent, poll at ${fmtWhen(fromLocalInput(pollAt))}. You get a reminder then.`
        : `${m.name}: brochure and poll sent. Update its status within ${poll.pollHours} hours.`);
      onSaved();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  const choice = (on: boolean, color: string) => (on ? { borderColor: color, background: `${color}10` } : { borderColor: '#e5e7eb', background: '#fff' });
  return (
    <Modal title={`How did it go: ${m.name}`} sub={`${m.when} · booked by ${m.bookedByName}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Did they join?">
          <button type="button" role="radio" aria-checked={joined === 'yes'} onClick={() => setJoined('yes')} className="rounded-2xl border-2 px-4 py-3 text-left" style={choice(joined === 'yes', '#15803d')}>
            <span className="block text-xl" aria-hidden>✅</span><span className="block font-bold text-gray-900">Joined</span><span className="block text-xs text-gray-500">Send the brochure, fix the poll time</span>
          </button>
          <button type="button" role="radio" aria-checked={joined === 'no'} onClick={() => setJoined('no')} className="rounded-2xl border-2 px-4 py-3 text-left" style={choice(joined === 'no', '#b91c1c')}>
            <span className="block text-xl" aria-hidden>🚫</span><span className="block font-bold text-gray-900">Didn't join</span><span className="block text-xs text-gray-500">To the salesperson's No Response</span>
          </button>
        </div>
        {joined === 'yes' && (
          <div className="space-y-3">
            <div className="flex gap-2" role="radiogroup" aria-label="What next">
              {([['poll', '📄 Brochure sent + poll time'], ['not-interested', '❌ Not interested']] as const).map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={next === k} onClick={() => setNext(k)}
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border-2 ${next === k ? 'border-purple-700 bg-purple-50 text-purple-900' : 'border-gray-200 text-gray-600'}`}>{label}</button>
              ))}
            </div>
            {next === 'poll' ? (
              <>
                <p className="text-xs text-gray-600">✓ The brochure goes now. Then the poll, at the time you fixed with the student.</p>
                <PollTime id="mo-poll" value={pollAt} onChange={setPollAt} poll={poll} />
              </>
            ) : <ReasonPick value={reason} other={other} onValue={setReason} onOther={setOther} />}
          </div>
        )}
        {joined && <div><label className={labelCls} htmlFor="mo-note">Note (optional)</label><textarea id="mo-note" rows={2} value={note} onChange={e => setNote(e.target.value)} className={`${inputCls} resize-none`} placeholder="How the meeting went" /></div>}
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Back</button>
          <button type="button" onClick={save} disabled={saving || !joined} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={purpleBg}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
};

// ── The poll's status and the payment, for a lead they handle ──

const STATUSES: { key: CoordStatus; icon: string; hint: string; on: string }[] = [
  { key: 'Interested', icon: '✅', hint: 'They want to join. Mark them Converted when they pay.', on: '#047857' },
  { key: 'Not Interested', icon: '❌', hint: 'They said no. Pick why.', on: '#b91c1c' },
  { key: 'No Response', icon: '🔕', hint: 'No reply yet.', on: '#475569' },
  { key: 'Poll Sent', icon: '📊', hint: 'You sent the poll again, or at another time than fixed.', on: '#a21caf' },
];

const CoordPollModal: React.FC<{ auth: CoordAuth; lead: { id: string; name: string; stage: string }; initial?: CoordStatus; poll: PollRules; onClose: () => void; onSaved: () => void }> = ({ auth, lead, initial, poll, onClose, onSaved }) => {
  const [status, setStatus] = useState<CoordStatus | ''>(initial || '');
  const [payAt, setPayAt] = useState('');
  const [pollAt, setPollAt] = useState(minutesAgo(0));
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!status) { toast.error('Pick the poll status'); return; }
    const why = reason === 'Other' ? other.trim() : reason;
    if (status === 'Not Interested' && !why) { toast.error('Say why they are not interested'); return; }
    if (status === 'Interested' && payAt && new Date(fromLocalInput(payAt)).getTime() < Date.now() - 5 * 60000) { toast.error('The payment date is in the past'); return; }
    if (status === 'Poll Sent' && pollTimeProblem(pollAt)) { toast.error(pollTimeProblem(pollAt)); return; }
    setSaving(true);
    try {
      const r = await meet.pollStatus(auth, {
        leadId: lead.id, status, note: note.trim(),
        ...(status === 'Not Interested' ? { reason: why } : {}),
        ...(status === 'Interested' && payAt ? { nextAt: fromLocalInput(payAt) } : {}),
        ...(status === 'Poll Sent' ? { pollSentAt: fromLocalInput(pollAt) } : {}),
      });
      toast.success(`${lead.name}: ${status}${status === 'Interested' && r.lead.nextAt ? `, pays ${fmtWhen(r.lead.nextAt)}` : ''}`);
      onSaved();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Poll status: ${lead.name}`} sub={`Now ${lead.stage}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid gap-2" role="radiogroup" aria-label="Poll status">
          {STATUSES.map(a => {
            const on = status === a.key;
            return (
              <button key={a.key} type="button" role="radio" aria-checked={on} onClick={() => setStatus(a.key)}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border-2 text-left" style={on ? { borderColor: a.on, background: `${a.on}10` } : { borderColor: '#e5e7eb', background: '#fff' }}>
                <span className="text-2xl" aria-hidden>{a.icon}</span>
                <span className="flex-1 min-w-0"><span className="block font-bold text-gray-900">{a.key === 'Poll Sent' ? 'Poll sent again / another time' : a.key}</span><span className="block text-xs text-gray-500">{a.hint}</span></span>
              </button>
            );
          })}
        </div>
        {status === 'Interested' && (
          <div>
            <label className={labelCls} htmlFor="cp-pay">When will they pay? (optional)</label>
            <input id="cp-pay" type="datetime-local" value={payAt} onChange={e => setPayAt(e.target.value)} className={inputCls} />
            <p className="text-[11px] text-gray-400 mt-1.5">With a date you get a reminder then.</p>
          </div>
        )}
        {status === 'Poll Sent' && <PollTime id="cp-poll" value={pollAt} onChange={setPollAt} poll={poll} />}
        {status === 'Not Interested' && <ReasonPick value={reason} other={other} onValue={setReason} onOther={setOther} />}
        {status && <div><label className={labelCls} htmlFor="cp-note">Note (optional)</label><textarea id="cp-note" rows={2} value={note} onChange={e => setNote(e.target.value)} className={`${inputCls} resize-none`} placeholder="What they said" /></div>}
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Back</button>
          <button type="button" onClick={save} disabled={saving || !status} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={purpleBg}>{saving ? 'Saving…' : 'Save poll status'}</button>
        </div>
      </div>
    </Modal>
  );
};

// They paid: Converted, one tap. The payment record is the registration form
// the student fills on the website, linked by their phone number or email
const ConvertModal: React.FC<{ auth: CoordAuth; lead: { id: string; name: string; paymentLinked?: boolean }; onClose: () => void; onSaved: () => void }> = ({ auth, lead, onClose, onSaved }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await meet.converted(auth, { leadId: lead.id, note: note.trim() });
      toast.success(r.lead.paymentLinked ? `🎉 ${lead.name} converted` : `🎉 ${lead.name} converted. Waiting for their registration form.`);
      onSaved();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`They paid: ${lead.name}`} sub="Converted" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-gray-700 bg-green-50 rounded-xl p-3">
          {lead.paymentLinked
            ? '✓ Their registration form is in: it is their payment record.'
            : 'Their payment record comes from the registration form they fill on the website. Until it comes in they show under "Converted, waiting for the form".'}
          {' '}Once they have paid the whole before-placement fee (₹15,000 online · ₹20,000 offline) you earn 10% of it.
        </p>
        <div><label className={labelCls} htmlFor="cv-note">Note (optional)</label><input id="cv-note" value={note} onChange={e => setNote(e.target.value)} className={inputCls} /></div>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Back</button>
          <button type="button" onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60">{saving ? 'Saving…' : '✅ Mark Converted'}</button>
        </div>
      </div>
    </Modal>
  );
};

// ── One lead, as its coordinator sees it ─────────────────────

const typeText = (a: CrmActivity) => {
  switch (a.type) {
    case 'meeting':  return meetingLabel(a);
    case 'poll':     return `${a.byName}: poll status → ${a.result}`;
    case 'call':     return `${a.byName}: ${a.outcome}${a.result ? ` → ${a.result}` : ''}`;
    case 'added':    return `Added by ${a.byName}${a.source ? ` · ${a.source}` : ''}`;
    case 'assigned': return `Given to ${a.toName}`;
    case 'moved':    return `Moved${a.fromName ? ` from ${a.fromName}` : ''} to ${a.toName}`;
    case 'enquired': return `Enquired again${a.source ? ` through ${a.source}` : ''}`;
    default:         return `${a.byName}: ${a.type}`;
  }
};

const FileButton: React.FC<{ auth: CoordAuth; a: CrmActivity; file: 'recording' | 'screenshot' }> = ({ auth, a, file }) => {
  const [url, setUrl] = useState('');
  const info = a[file]!;
  const open = async () => {
    try {
      const r = await meet.fileLink(auth, a.id, file);
      if (file === 'screenshot') window.open(crmFileUrl(r.url), '_blank', 'noopener');
      else setUrl(crmFileUrl(r.url));
    } catch (e: any) { toast.error(e.message); }
  };
  if (url) return <audio controls autoPlay src={url} className="w-full max-w-sm mt-2" />;
  return (
    <button type="button" onClick={open} className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50">
      {file === 'recording' ? '▶ Play the call' : '🖼 View screenshot'} <span className="font-medium text-gray-400">{info.fileName}</span>
    </button>
  );
};

const CoordLeadModal: React.FC<{ auth: CoordAuth; leadId: string; onClose: () => void; onChanged: () => void }> = ({ auth, leadId, onClose, onChanged }) => {
  const [data, setData] = useState<CoordLeadData | null>(null);
  const [polling, setPolling] = useState<CoordStatus | '' | null>(null);
  const [paying, setPaying] = useState(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const load = useCallback(async () => {
    try { setData(await meet.lead(auth, leadId)); } catch (e: any) { toast.error(e.message); closeRef.current(); }
  }, [auth, leadId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Modal title="Lead" onClose={onClose}><Spinner label="Loading…" /></Modal>;
  const l = data.lead;
  const m = data.meetings.find(x => x.active) || data.meetings[0] || null;
  const done = () => { setPolling(null); setPaying(false); load(); onChanged(); };
  return (
    <>
      <Modal title={l.name || 'Student'} sub={`#${l.leadNo || ''} · ${l.ownerName || ''}`} onClose={onClose} wide>
        <div className="space-y-5">
          <div className="flex items-center gap-2 flex-wrap"><StageChip stage={l.stage} />{l.mode && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">{l.mode}</span>}</div>
          {m && (
            <div className="rounded-xl bg-indigo-50 px-3 py-2 text-sm">
              <p className="text-indigo-900"><b>Meeting:</b> {m.when} · {m.active ? (m.link ? `${m.app || 'link'} ✓` : 'no link yet') : m.status === 'Not Joined' ? "didn't join" : m.status.toLowerCase()}</p>
              {m.link && m.active && <a href={m.link} target="_blank" rel="noreferrer" className="text-xs font-semibold text-indigo-700 break-all">{m.link}</a>}
              {m.active && <JoinCode m={m} />}
            </div>
          )}
          {!data.full ? (
            <p className="text-sm text-amber-800 bg-amber-50 rounded-xl p-3">Add the meeting link to see the student's details and the call log.</p>
          ) : (
            <>
              <div className="flex gap-2 flex-wrap">
                {l.phone && <a href={`tel:+91${l.phone}`} className="px-4 py-2.5 rounded-xl text-sm font-bold text-white" style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>📞 Call {fmtPhone(l.phone)}</a>}
                {l.phone && <a href={whatsappUrl(`91${l.phone}`)} target="_blank" rel="noreferrer" className="px-4 py-2.5 rounded-xl text-sm font-bold border-2 border-green-200 text-green-800 hover:bg-green-50">💬 WhatsApp</a>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {[['Degree', l.degree], ['Passed out', l.passedOutYear], ['Profile', l.profile], ['Course', l.course], ['Email', l.email], ['Location', l.location], ['Source', l.source], ['Reason', l.reason]]
                  .filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="min-w-0"><p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{k}</p><p className="text-gray-800 font-medium break-words">{v}</p></div>
                  ))}
              </div>
              {l.pitchNotes && <p className="text-sm text-gray-600"><b className="text-gray-400 text-xs uppercase">About the student:</b> {l.pitchNotes}</p>}
            </>
          )}
          {l.stage === 'Converted' && data.handles && (
            l.paymentLinked
              ? <p className="text-sm text-green-800 bg-green-50 rounded-xl p-3">🎉 Converted. Their registration form is in{l.formAt ? ` (${fmtWhen(l.formAt)})` : ''}.</p>
              : <p className="text-sm text-amber-900 bg-amber-50 rounded-xl p-3">🎉 Converted. Waiting for their registration form: remind them to fill it. It is their payment record.</p>
          )}
          {data.canUpdate && (
            <div className="rounded-2xl p-4 bg-fuchsia-50 border-2 border-fuchsia-100 space-y-3">
              <p className="font-bold text-fuchsia-900">{l.stage === 'Poll Sent' ? (l.pollLater ? `Send the poll at ${fmtWhen(l.pollSentAt || null)}` : l.pollDue ? 'Update the poll status now' : `Update the poll status by ${fmtWhen(l.nextAt || null)}`)
                : l.stage === 'Interested' ? (l.nextAt ? `Interested: pays ${fmtWhen(l.nextAt)}` : 'Interested: waiting for the payment') : `Now: ${l.stage}`}</p>
              {l.paymentLinked && <p className="text-xs font-semibold text-green-800">📝 They filled the registration form{l.formAt ? ` ${fmtWhen(l.formAt)}` : ''}. Mark them Converted once they have paid.</p>}
              <div className="flex flex-wrap gap-2">
                {l.stage === 'Poll Sent' && l.pollLater && <button type="button" onClick={() => setPolling('Poll Sent')} className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-fuchsia-200 bg-white text-fuchsia-800">Change the poll time</button>}
                <button type="button" onClick={() => setPolling('')} className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-gray-200 bg-white text-gray-700">Update the poll status</button>
                <button type="button" onClick={() => setPaying(true)} className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-green-600 hover:bg-green-700">💰 They paid: Converted</button>
              </div>
            </div>
          )}
          {data.history.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Call log</p>
              <ol className="space-y-2">
                {data.history.map(a => (
                  <li key={a.id} className="flex gap-3 text-sm">
                    <span className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs" aria-hidden>{ICON[a.type] || '•'}</span>
                    <div className="flex-1 min-w-0 pb-2 border-b border-gray-100">
                      <div className="flex justify-between gap-2 flex-wrap"><p className="font-semibold text-gray-800">{typeText(a)}</p><p className="text-[11px] text-gray-400 whitespace-nowrap">{fmtWhen(a.at)}</p></div>
                      {a.visit && <p className="text-xs text-gray-500">Visit: {a.visit === 'came' ? 'they came' : "they didn't come"}</p>}
                      {a.reason && <p className="text-xs text-gray-500">{a.reason}</p>}
                      {a.pollSentAt && <p className="text-xs text-gray-500">{pollLabel(a.pollSentAt)}</p>}
                      {a.nextAt && <p className="text-xs text-gray-500">{nextLabel(a)}: {fmtWhen(a.nextAt)}</p>}
                      {a.amount > 0 && <p className="text-xs text-green-700 font-semibold">Paid {rupees(a.amount)}</p>}
                      {a.note && <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{a.note}</p>}
                      {a.recording && <FileButton auth={auth} a={a} file="recording" />}
                      {a.screenshot && <FileButton auth={auth} a={a} file="screenshot" />}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </Modal>
      {polling !== null && <CoordPollModal auth={auth} lead={{ id: l.id, name: l.name, stage: l.stage }} initial={polling || undefined} poll={data} onClose={() => setPolling(null)} onSaved={done} />}
      {paying && <ConvertModal auth={auth} lead={{ id: l.id, name: l.name, paymentLinked: l.paymentLinked }} onClose={() => setPaying(false)} onSaved={done} />}
    </>
  );
};

// ── One meeting, for its coordinator ─────────────────────────

const CoordMeetingCard: React.FC<{ auth: CoordAuth; m: Meeting; poll: PollRules; onChanged: () => void; onOpen: (leadId: string) => void }> = ({ auth, m, poll, onChanged, onOpen }) => {
  const [link, setLink] = useState(m.link);
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Saved: the student's page opens, with their details and the call log. An
  // open meeting is theirs once their link is in first; someone faster gets it otherwise.
  const saveLink = async () => {
    setSaving(true);
    try {
      const r = await meet.setLink(auth, m.id, link.trim());
      setLink(r.meeting.link);
      toast.success(`${r.taken ? 'The meeting is yours. ' : ''}${r.meeting.app || 'Link'} saved. ${r.meeting.salesPersonName || 'The salesperson'} sends it to ${m.name}.`);
      onChanged();
      onOpen(m.leadId);
    } catch (e: any) {
      toast.error(e.message);
      if (m.open) onChanged();   // taken by someone else: off the list
    } finally { setSaving(false); }
  };
  return (
    <div className={`bg-white rounded-2xl border-2 p-4 space-y-3 ${m.active && (m.status === 'Booked' || m.started) ? 'border-red-200' : 'border-gray-100'}`}>
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={() => onOpen(m.leadId)} className="text-left min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-bold text-gray-800">{m.name || 'Student'}</span><MeetingChip m={m} coordinator /></div>
          <p className="text-xs text-gray-500 mt-0.5">{m.course}{m.mode ? ` · ${m.mode}` : ''}{m.phone ? ` · ${fmtPhone(m.phone)}` : ''}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">Booked by {m.bookedByName}{m.salesPersonName && m.salesPersonName !== m.bookedByName ? ` for ${m.salesPersonName}` : ''}</p>
        </button>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-indigo-900">{m.dayLabel}</p>
          <p className="text-xs font-semibold text-gray-600">{m.timeLabel}</p>
        </div>
      </div>
      {m.open && m.active ? (
        <p className="text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2">Open meeting: every coordinator free then can take it. <b>The first to add the meeting link gets it</b>, and then sees the student's details and the call log.</p>
      ) : m.detailsLocked ? (
        <p className="text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2">Add the meeting link: then you see the student's details and the call log.</p>
      ) : m.active && (
        <div className="text-xs text-gray-600 grid grid-cols-2 gap-x-3 gap-y-1">
          {[['Degree', m.degree], ['Passed out', m.passedOutYear], ['Profile', m.profile], ['Location', m.location]].filter(([, v]) => v).map(([k, v]) => <p key={k}><span className="text-gray-400">{k}:</span> {v}</p>)}
          {m.pitchNotes && <p className="col-span-2"><span className="text-gray-400">About the student:</span> {m.pitchNotes}</p>}
        </div>
      )}
      {m.active && !m.ended && (
        <div className="space-y-1.5">
          <div className="flex gap-2 items-start">
            {/* A box that keeps line breaks: a pasted Teams invite stays readable for the link, ID and passcode */}
            <textarea value={link} onChange={e => setLink(e.target.value)} rows={m.link ? 1 : 2} placeholder="Paste the Google Meet, Zoom or Teams link (or the whole Teams invite)"
              className={`${inputCls} flex-1 resize-y`} aria-label="Meeting link" />
            <button type="button" onClick={saveLink} disabled={saving || !link.trim() || link.trim() === m.link} className="px-4 py-2.5 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={purpleBg}>{saving ? 'Saving…' : m.link ? 'Change' : m.open ? 'Take it: submit link' : 'Submit link'}</button>
          </div>
          {m.link && <div className="text-[11px] text-green-800">✓ {m.app || 'Link'} saved<JoinCode m={m} /></div>}
        </div>
      )}
      {m.active && m.status !== 'Booked' && !m.started && (
        <p className="text-[11px] text-gray-500">{m.status === 'Confirmed' ? `Sent to the student${m.confirmedByName ? ` by ${m.confirmedByName}` : ''}` : `Waiting for ${m.salesPersonName || 'the salesperson'} to send it to the student`}</p>
      )}
      {/* Coming up: what comes after the meeting, ready once it starts */}
      {m.active && m.status !== 'Booked' && !m.started && (
        <p className="text-[11px] text-indigo-800 bg-indigo-50 rounded-lg px-3 py-2">After the meeting: <b>Did they join?</b> → Joined → send the brochure and fix the poll time. It opens when the meeting starts.</p>
      )}
      {!m.active && (
        <p className="text-xs text-gray-600">{m.status === 'Joined' ? `Joined${m.note ? `: ${m.note}` : ''}` : m.status === 'Not Joined' ? `Didn't join${m.note ? `: ${m.note}` : ''}` : `Cancelled by ${m.cancelledByName}: ${m.cancelReason}`}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {m.active && m.started && <button type="button" onClick={() => setMarking(true)} className="px-4 py-2 rounded-xl text-xs font-bold text-white" style={purpleBg}>Did they join?</button>}
        {m.active && m.status !== 'Booked' && !m.started && <button type="button" disabled title="Opens when the meeting starts" className="px-4 py-2 rounded-xl text-xs font-bold text-white opacity-40 cursor-not-allowed" style={purpleBg}>Did they join?</button>}
        {!m.detailsLocked && m.waPhone && <a href={whatsappUrl(m.waPhone)} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-green-200 text-green-800 hover:bg-green-50">💬 WhatsApp the student</a>}
        {m.link && m.active && <a href={m.link} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-indigo-200 text-indigo-800 hover:bg-indigo-50">Open the meeting</a>}
        <button type="button" onClick={() => onOpen(m.leadId)} className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-gray-200 text-gray-700 hover:bg-gray-50">📋 Details &amp; call log</button>
        {m.active && !m.open && <button type="button" onClick={() => setCancelling(true)} className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-red-100 text-red-700 hover:bg-red-50">Cancel</button>}
      </div>
      {marking && <OutcomeModal auth={auth} m={m} poll={poll} onClose={() => setMarking(false)} onSaved={() => { setMarking(false); onChanged(); }} />}
      {cancelling && (
        <CancelMeetingModal m={m} reasons={["I'm not available then", 'Student asked to cancel', 'Booked by mistake']} onClose={() => setCancelling(false)}
          onCancel={async reason => { await meet.cancel(auth, m.id, reason); toast.success(`Cancelled. ${m.salesPersonName || 'The salesperson'} books another time.`); setCancelling(false); onChanged(); }} />
      )}
    </div>
  );
};

// What is next for a lead they handle, in a few words (red: due now)
function leadNext(l: CrmLead): { text: string; due: boolean } {
  if (l.stage === 'Converted') return { text: 'Converted: waiting for their registration form', due: false };
  if (l.stage === 'Poll Sent') {
    if (l.pollLater) return { text: `Send the poll at ${fmtWhen(l.pollSentAt)}`, due: false };
    return l.pollDue ? { text: 'Update the poll now', due: true } : { text: `Update the poll by ${fmtWhen(l.nextAt)}`, due: false };
  }
  if (l.stage === 'Interested') return l.paymentDue ? { text: 'Payment due: check it', due: true } : { text: l.nextAt ? `Pays ${fmtWhen(l.nextAt)}` : 'Waiting to pay', due: false };
  return { text: 'No reply to the poll', due: false };
}

const LeadLine: React.FC<{ l: CrmLead; onOpen: () => void; onPoll: (s: CoordStatus) => void; onPaid: () => void }> = ({ l, onOpen, onPoll, onPaid }) => {
  const next = leadNext(l);
  const formIn = l.paymentLinked && l.stage !== 'Converted';
  const btn = 'px-3 py-1.5 rounded-xl text-[11px] font-bold';
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 space-y-2">
      <button type="button" onClick={onOpen} className="w-full text-left flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-bold text-gray-800">{l.name || 'Student'}</span><StageChip stage={l.stage} small /></div>
          <p className="text-xs text-gray-500 mt-0.5">{fmtPhone(l.phone)}{l.course ? ` · ${l.course}` : ''}{l.mode ? ` · ${l.mode}` : ''} · {l.ownerName}</p>
        </div>
        <p className={`text-xs font-bold text-right ${next.due ? 'text-red-600' : l.pollLater ? 'text-fuchsia-700' : 'text-gray-500'}`}>{next.text}</p>
      </button>
      {formIn && <p className="text-xs font-semibold text-green-800 bg-green-50 rounded-lg px-3 py-1.5">📝 They filled the registration form{l.formAt ? ` ${fmtWhen(l.formAt)}` : ''}: mark them Converted once they have paid</p>}
      {l.stage === 'Converted' ? (
        <p className="text-xs text-amber-900">Remind them to fill the registration form: it is their payment record.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {l.stage === 'Poll Sent' && l.pollLater && <button type="button" onClick={() => onPoll('Poll Sent')} className={`${btn} border-2 border-fuchsia-200 text-fuchsia-800`}>📊 Change the poll time</button>}
          {(l.stage === 'Interested' || formIn) && <button type="button" onClick={onPaid} className={`${btn} bg-green-600 hover:bg-green-700 text-white`}>💰 They paid: Converted</button>}
          {/* Also before the poll time: the student may answer first */}
          {l.stage !== 'Interested' && <button type="button" onClick={() => onPoll('Interested')} className={`${btn} ${formIn ? 'border-2 border-green-200 text-green-800' : 'bg-green-600 hover:bg-green-700 text-white'}`}>✅ Interested</button>}
          {l.stage !== 'Interested' && <button type="button" onClick={() => onPoll('Not Interested')} className={`${btn} border-2 border-red-200 text-red-700`}>❌ Not interested</button>}
          {l.stage === 'Poll Sent' && !l.pollLater && <button type="button" onClick={() => onPoll('No Response')} className={`${btn} border-2 border-gray-200 text-gray-700`}>🔕 No response</button>}
          {l.stage === 'No Response' && <button type="button" onClick={() => onPoll('Poll Sent')} className={`${btn} border-2 border-fuchsia-200 text-fuchsia-800`}>📊 Sent the poll again</button>}
          {l.stage === 'Interested' && <button type="button" onClick={() => onPoll('Not Interested')} className={`${btn} border-2 border-gray-200 text-gray-700`}>Changed their mind</button>}
        </div>
      )}
    </div>
  );
};

const Section: React.FC<{ title: string; sub?: string; count: number; tone?: string; children: React.ReactNode }> = ({ title, sub, count, tone = 'text-gray-800', children }) => (
  count ? (
    <div className="space-y-2" role="region" aria-label={title}>
      <div className="px-1"><h2 className={`text-sm font-bold ${tone}`}>{title} <span className="text-gray-400 font-semibold">· {count}</span></h2>{sub && <p className="text-xs text-gray-400">{sub}</p>}</div>
      <div className="grid gap-2 lg:grid-cols-2">{children}</div>
    </div>
  ) : null
);

export const CoordinatorMeetings: React.FC = () => {
  const auth = useCoordAuth();
  const [data, setData] = useState<MineData | null>(null);
  const [tab, setTab] = useState('todo');
  const [open, setOpen] = useState<string | null>(null);
  const [poll, setPoll] = useState<{ lead: CrmLead; status: CoordStatus } | null>(null);
  const [paid, setPaid] = useState<CrmLead | null>(null);
  const load = useCallback(async () => {
    if (!auth) return;
    try { setData(await meet.mine(auth)); } catch (e: any) { toast.error(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.coordToken]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);

  const body = () => {
    if (!auth) return <SignInAgain />;
    if (!data) return <Spinner label="Loading your meetings…" />;
    const card = (m: Meeting) => <CoordMeetingCard key={m.id} auth={auth} m={m} poll={data} onChanged={load} onOpen={setOpen} />;
    const line = (l: CrmLead) => <LeadLine key={l.id} l={l} onOpen={() => setOpen(l.id)} onPoll={s => setPoll({ lead: l, status: s })} onPaid={() => setPaid(l)} />;
    const c = data.counts;
    const toSend = data.toSend || [], waitingForm = data.waitingForm || [];
    // needLink also lists the open meetings (for older screens)
    const openList = data.needLink.filter(m => m.open), needLink = data.needLink.filter(m => !m.open);
    const leadCount = c.polls + c.interested + c.noResponse + toSend.length + waitingForm.length;
    return (
      <div className="space-y-4">
        {!data.hasTimes && (
          <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-amber-900"><b>Set your free times</b> so salespeople can book meetings with you.</p>
            <Link to="/tracker/coordinator/meeting-times" className="px-4 py-2 rounded-xl text-xs font-bold text-white" style={purpleBg}>Set meeting times</Link>
          </div>
        )}
        <Tabs label="Meetings" value={tab} onChange={setTab} tabs={[
          { id: 'todo', label: 'To do', badge: c.needLink + c.toMark },
          { id: 'coming', label: c.upcoming ? `Coming up · ${c.upcoming}` : 'Coming up', badge: 0 },
          { id: 'leads', label: 'My leads', badge: c.pollsDue + c.paymentsDue + (c.formIn || 0) },
          { id: 'past', label: 'Past 30 days' },
        ]} />
        {tab === 'todo' && (c.needLink + c.toMark === 0 ? <Empty icon="🎉" title="Nothing to do right now" sub="New meetings to take or add a link to, and meetings to mark, show up here." /> : (
          <div className="space-y-5">
            <Section title="Did they join?" tone="text-red-700" sub="The meeting has started or is over. Mark what happened." count={data.toMark.length}>{data.toMark.map(card)}</Section>
            <Section title="Open meetings: the first link takes it" tone="text-red-700" sub="Booked at a time you are free. Every coordinator free then sees it; whoever adds the link first gets it." count={openList.length}>{openList.map(card)}</Section>
            <Section title="Add the meeting link" tone="text-red-700" sub="The salesperson sends it to the student once it is in." count={needLink.length}>{needLink.map(card)}</Section>
          </div>
        ))}
        {tab === 'coming' && (data.upcoming.length === 0 ? <Empty icon="📅" title="No meetings coming up" sub="Meetings with a link show up here until they start." /> : <div className="grid gap-2 lg:grid-cols-2">{data.upcoming.map(card)}</div>)}
        {tab === 'leads' && (leadCount === 0 ? <Empty icon="🤝" title="No leads with you yet" sub="When a student joins your meeting, you handle their poll and payment here." /> : (
          <div className="space-y-5">
            <Section title="Polls to send" tone="text-fuchsia-800" sub="At the time you fixed with the student: you get a reminder then. If they answer before it, mark Interested or Not interested." count={toSend.length}>{toSend.map(line)}</Section>
            <Section title="Update the poll status" tone={c.pollsDue ? 'text-red-700' : 'text-fuchsia-800'} sub={`Due ${data.pollHours} hours after the poll went out`} count={data.polls.length}>{data.polls.map(line)}</Section>
            <Section title="Waiting for payment" tone="text-green-800" sub="When they pay, mark them Converted" count={data.interested.length}>{data.interested.map(line)}</Section>
            <Section title="No response" count={data.noResponse.length}>{data.noResponse.map(line)}</Section>
            <Section title="Converted, waiting for the form" tone="text-amber-800" sub="Their payment record comes from the registration form they fill on the website" count={waitingForm.length}>{waitingForm.map(line)}</Section>
          </div>
        ))}
        {tab === 'past' && (data.past.length === 0 ? <Empty icon="🗂️" title="No past meetings" /> : <div className="grid gap-2 lg:grid-cols-2">{data.past.map(card)}</div>)}
      </div>
    );
  };

  return (
    <TrackerLayout>
      <div className="space-y-5">
        <Header icon="🤝" title="Sales Meetings" sub="Meetings salespeople booked at your free times: add your link to take one (the first link gets it), meet the student, mark whether they joined, then follow their poll and payment." right={auth ? refreshBtn(load) : undefined} />
        {body()}
      </div>
      {auth && open && <CoordLeadModal auth={auth} leadId={open} onClose={() => setOpen(null)} onChanged={load} />}
      {auth && poll && data && <CoordPollModal auth={auth} lead={{ id: poll.lead.id, name: poll.lead.name, stage: poll.lead.stage }} initial={poll.status} poll={data} onClose={() => setPoll(null)} onSaved={() => { setPoll(null); load(); }} />}
      {auth && paid && <ConvertModal auth={auth} lead={{ id: paid.id, name: paid.name, paymentLinked: paid.paymentLinked }} onClose={() => setPaid(null)} onSaved={() => { setPaid(null); load(); }} />}
    </TrackerLayout>
  );
};

// ── Meeting Times ────────────────────────────────────────────

type Range = { day: number; from: string; to: string };

export const CoordinatorMeetingTimes: React.FC = () => {
  const auth = useCoordAuth();
  const [data, setData] = useState<MeetTimesData | null>(null);
  const [times, setTimes] = useState<Range[]>([]);
  const [off, setOff] = useState<string[]>([]);
  const [adding, setAdding] = useState<Record<number, { from: string; to: string }>>({});
  const [offDate, setOffDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const take = (r: MeetTimesData) => { setData(r); setTimes(r.times); setOff(r.off.map(o => o.date)); setDirty(false); };
  const load = useCallback(async () => {
    if (!auth) return;
    try { take(await meet.times(auth)); } catch (e: any) { toast.error(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.coordToken]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!auth) return;
    setSaving(true);
    try {
      const r = await meet.setTimes(auth, times, off);
      take(r);
      toast.success(`Saved: ${r.slotsPerWeek} one-hour meeting${r.slotsPerWeek === 1 ? '' : 's'} a week`);
      if (r.outside?.length) toast.warn(`${r.outside.length} meeting${r.outside.length === 1 ? ' is' : 's are'} outside your new times. Cancel ${r.outside.length === 1 ? 'it' : 'them'} on Sales Meetings if you can't make it.`, { autoClose: 9000 });
      setData(d => d && { ...d, outside: r.outside });
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };
  const addRange = (day: number) => {
    const a = adding[day];
    if (!a?.from || !a?.to) { toast.error('Pick the start and end time'); return; }
    setTimes(t => [...t, { day, from: a.from, to: a.to }].sort((x, y) => x.day - y.day || x.from.localeCompare(y.from)));
    setAdding(s => ({ ...s, [day]: { from: '', to: '' } }));
    setDirty(true);
  };
  const slotsIn = (r: Range) => { const [fh, fm] = r.from.split(':').map(Number), [th, tm] = r.to.split(':').map(Number); return Math.max(0, Math.floor(((th * 60 + tm) - (fh * 60 + fm)) / 60)); };

  return (
    <TrackerLayout>
      <div className="space-y-5 max-w-4xl">
        <Header icon="🗓️" title="Meeting Times" sub="When you are free for sales meetings, every week. Your times are cut into 1-hour meetings; approved leave and holidays block them by themselves." right={auth ? refreshBtn(load) : undefined} />
        {!auth ? <SignInAgain /> : !data ? <Spinner /> : (
          <>
            <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-bold text-gray-800">Every week</h2>
                <p className="text-xs text-gray-500">{times.reduce((n, r) => n + slotsIn(r), 0)} one-hour meetings a week</p>
              </div>
              {data.weekdays.map(w => {
                const mine = times.filter(t => t.day === w.day);
                const a = adding[w.day] || { from: '', to: '' };
                return (
                  <div key={w.day} className="flex flex-col sm:flex-row sm:items-center gap-2 border-t border-gray-50 pt-3">
                    <p className="w-28 text-sm font-bold text-gray-700">{w.name}</p>
                    <div className="flex-1 flex flex-wrap items-center gap-2">
                      {mine.length === 0 && <span className="text-xs text-gray-400">Not free</span>}
                      {mine.map(r => (
                        <span key={`${r.from}-${r.to}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-900">
                          {fmtHm(r.from)} – {fmtHm(r.to)} <span className="text-indigo-500 font-semibold">({slotsIn(r)})</span>
                          <button type="button" onClick={() => { setTimes(t => t.filter(x => x !== r)); setDirty(true); }} className="text-indigo-400 hover:text-red-600" aria-label={`Remove ${w.name} ${r.from}`}>×</button>
                        </span>
                      ))}
                      <span className="inline-flex items-center gap-1">
                        <input type="time" value={a.from} onChange={e => setAdding(s => ({ ...s, [w.day]: { ...a, from: e.target.value } }))} className="px-2 py-1 border-2 border-gray-200 rounded-lg text-xs" aria-label={`${w.name} from`} />
                        <span className="text-xs text-gray-400">to</span>
                        <input type="time" value={a.to} onChange={e => setAdding(s => ({ ...s, [w.day]: { ...a, to: e.target.value } }))} className="px-2 py-1 border-2 border-gray-200 rounded-lg text-xs" aria-label={`${w.name} to`} />
                        <button type="button" onClick={() => addRange(w.day)} className="px-2.5 py-1 rounded-lg text-xs font-bold border-2 border-purple-200 text-purple-700 hover:bg-purple-50">＋ Add</button>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 space-y-3">
              <h2 className="font-bold text-gray-800">Not free on</h2>
              <p className="text-xs text-gray-500">Days you can't take meetings (leave you applied for is blocked already).</p>
              <div className="flex flex-wrap gap-2">
                {off.length === 0 && <span className="text-xs text-gray-400">None</span>}
                {off.map(d => (
                  <span key={d} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-50 text-amber-900">
                    {new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' })}
                    <button type="button" onClick={() => { setOff(o => o.filter(x => x !== d)); setDirty(true); }} className="text-amber-500 hover:text-red-600" aria-label={`Remove ${d}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="date" value={offDate} onChange={e => setOffDate(e.target.value)} className="px-3 py-2 border-2 border-gray-200 rounded-xl text-sm" aria-label="Date" />
                <button type="button" onClick={() => { if (offDate && !off.includes(offDate)) { setOff(o => [...o, offDate].sort()); setDirty(true); } setOffDate(''); }} className="px-3 py-2 rounded-xl text-xs font-bold border-2 border-gray-200 text-gray-700">＋ Add date</button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={save} disabled={saving || !dirty} className="px-6 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={purpleBg}>{saving ? 'Saving…' : 'Save my times'}</button>
              {dirty && <span className="text-xs text-amber-700 font-semibold">Not saved yet</span>}
            </div>
            {!!data.outside?.length && (
              <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <p className="font-bold">Still booked outside your new times:</p>
                <ul className="list-disc pl-5 mt-1">{data.outside.map(o => <li key={o.id}>{o.name}, {o.when}</li>)}</ul>
                <p className="text-xs mt-1">They stay booked. Cancel them on Sales Meetings if you can't make them.</p>
              </div>
            )}
            <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 space-y-3">
              <h2 className="font-bold text-gray-800">The next working days, as salespeople see them</h2>
              <div className="grid gap-2 sm:grid-cols-2">
                {data.week.map(d => (
                  <div key={d.date} className="rounded-xl border border-gray-100 p-3">
                    <p className="text-xs font-bold text-gray-700 mb-1.5">{d.label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {d.booked.map(b => <span key={b.id} className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-indigo-100 text-indigo-900">{b.label} · {b.name}</span>)}
                      {d.free.map(f => <span key={f.slot} className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-50 text-green-800">{f.label} free</span>)}
                      {!d.booked.length && !d.free.length && <span className="text-[11px] text-gray-400">Nothing</span>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400">A time can be booked until {data.closeBefore} minutes before it starts.</p>
            </div>
          </>
        )}
      </div>
    </TrackerLayout>
  );
};

// ── Meeting Dashboard ────────────────────────────────────────

export const CoordinatorMeetingDashboard: React.FC = () => {
  const auth = useCoordAuth();
  return (
    <TrackerLayout>
      <div className="space-y-5">
        <Header icon="📈" title="Meeting Dashboard" sub="Your meetings and what came of them: who joined, polls, interested, converted, and your conversion rate." />
        {!auth ? <SignInAgain /> : <MeetNumbers load={f => meet.dashboard(auth, f)} />}
      </div>
    </TrackerLayout>
  );
};

// ── My Incentive ─────────────────────────────────────────────

const StatusTag: React.FC<{ r: IncentiveRow }> = ({ r }) => (
  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.status === 'Fully Paid' ? 'bg-green-100 text-green-800' : r.status === 'Ran Away' ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-800'}`}>{r.status}</span>
);

export const IncentiveRows: React.FC<{ rows: IncentiveRow[]; onMark?: (r: IncentiveRow) => void; busy?: string }> = ({ rows, onMark, busy }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
        <th className="py-2 pr-3">Student</th><th className="py-2 px-2 text-right">Fee</th><th className="py-2 px-2 text-right">Paid</th><th className="py-2 px-2">Status</th><th className="py-2 px-2 text-right">Incentive</th><th className="py-2 pl-2">Paid out</th>
      </tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id} className="border-t border-gray-100">
            <td className="py-2 pr-3"><p className="font-semibold text-gray-800">{r.name}</p><p className="text-[11px] text-gray-400">{r.course}{r.mode ? ` · ${r.mode}` : ''}{r.enrollDate ? ` · ${r.enrollDate}` : ''}</p></td>
            <td className="py-2 px-2 text-right">{rupees(r.courseFee)}</td>
            <td className="py-2 px-2 text-right">{rupees(r.totalPaid)}{r.balance > 0 && <span className="block text-[10px] text-amber-700">{rupees(r.balance)} to go</span>}</td>
            <td className="py-2 px-2"><StatusTag r={r} /></td>
            <td className="py-2 px-2 text-right font-bold">{r.incentive ? rupees(r.incentive) : r.waiting ? <span className="text-amber-700 font-semibold">{rupees(r.waiting)} waiting</span> : '—'}</td>
            <td className="py-2 pl-2">
              {r.paidOut ? <span className="text-xs font-bold text-green-700">✓ {r.paidOutAt ? new Date(r.paidOutAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</span> : r.incentive ? <span className="text-xs text-gray-500">Not yet</span> : ''}
              {onMark && r.status === 'Fully Paid' && (
                <button type="button" onClick={() => onMark(r)} disabled={busy === r.id} className={`ml-2 px-2.5 py-1 rounded-lg text-[11px] font-bold border disabled:opacity-50 ${r.paidOut ? 'border-gray-200 text-gray-500' : 'border-green-300 text-green-800 bg-green-50'}`}>{r.paidOut ? 'Undo' : 'Mark paid'}</button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CoordinatorIncentive: React.FC = () => {
  const auth = useCoordAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof meet.incentive>> | null>(null);
  const load = useCallback(async () => {
    if (!auth) return;
    try { setData(await meet.incentive(auth)); } catch (e: any) { toast.error(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.coordToken]);
  useEffect(() => { load(); }, [load]);
  return (
    <TrackerLayout>
      <div className="space-y-5 max-w-4xl">
        <Header icon="💰" title="My Incentive" sub="10% of the before-placement fee (₹15,000 online · ₹20,000 offline) for each student you convert after a meeting, earned once they have paid it in full." right={auth ? refreshBtn(load) : undefined} />
        {!auth ? <SignInAgain /> : !data ? <Spinner /> : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ['Earned', rupees(data.totals.earned), `${data.totals.fullyPaid} fully paid`],
                ['Paid out', rupees(data.totals.paidOut), ''],
                ['To be paid', rupees(data.totals.toBePaid), 'earned, not paid out yet'],
                ['Waiting', rupees(data.totals.waiting), 'when the fee is paid in full'],
              ].map(([k, v, s]) => (
                <div key={k} className="bg-white rounded-2xl border border-gray-100 px-4 py-3">
                  <p className="text-xs font-semibold text-gray-500">{k}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{v}</p>
                  {s && <p className="text-[11px] text-gray-400 mt-0.5">{s}</p>}
                </div>
              ))}
            </div>
            {data.rows.length === 0 ? <Empty icon="💰" title="No converted students yet" sub="Students you convert after a meeting show up here." /> : (
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
                <h2 className="font-bold text-gray-800 mb-3">Your students · {data.rows.length}</h2>
                <IncentiveRows rows={data.rows} />
              </div>
            )}
            {data.months.length > 1 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
                <h2 className="font-bold text-gray-800 mb-2">By month</h2>
                {data.months.map(m => (
                  <div key={m.month || 'none'} className="flex justify-between text-sm border-b border-gray-50 py-1.5">
                    <span className="text-gray-700">{m.month ? new Date(`${m.month}-01T00:00:00Z`).toLocaleDateString('en-IN', { timeZone: 'UTC', month: 'long', year: 'numeric' }) : 'No date'}</span>
                    <span className="text-gray-500 text-xs">{m.students} student{m.students === 1 ? '' : 's'} · <b className="text-gray-800">{rupees(m.earned)}</b>{m.waiting ? ` · ${rupees(m.waiting)} waiting` : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </TrackerLayout>
  );
};
